/**
 * Archivo: InfoColegial.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 20/08/2024
 * 
 * Descripción:
 * Este componente muestra la información detallada de un colegial y permite realizar acciones como marcarlo como favorito, editar sus datos y eliminarlo.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^3.0.1
 * - Servicios/ColegialesServicios: Servicio para actualizar los datos del colegial.
 * - componentes/VentanaEnviarMensaje: Componente para enviar mensajes.
 * - Imagenes/Iconos: Contiene los iconos utilizados en el componente.
 * 
 * Propósito:
 * Proporcionar una interfaz para visualizar y gestionar la información de un colegial.
 */

import React, { useState } from 'react';
import Cookies from 'js-cookie';
import VentanaEnviarMensaje from './VentanaEnviarMensaje';
import { actualizarColegial } from '../Servicios/ColegialesServicios';

import favoritosNegro from '../Imagenes/Iconos/FavoritosNegro.png'
import corazonRojo from '../Imagenes/Iconos/CorazonRojo.png'
import atrasNegro from '../Imagenes/Iconos/AtrasNegro.png'
import logoCerbunaBlanco from '../Imagenes/Iconos/LogoCerbunaBlanco.png'
import lapizBlanco from '../Imagenes/Iconos/LapizBlanco.png'
import lapizNegro from '../Imagenes/Iconos/LapizNegro.png'

const InfoColegial = ({onClose, permisos, colegial, manejarFavorito, actualizarLista, eliminarColegial}) => {
    const urlImagenes = `${process.env.REACT_APP_URL_IMAGENES}FotosColegiales/`
    const token = Cookies.get('token');

    //console.log("Mis permisos: ", permisos)

    const tienePrivilegios = (permisos === 'Conserjería' || permisos === 'Administrador')
    const esConser = permisos === 'Conserjería'
    const [mensajeError, setMensajeError] = useState('');
    const [mensajeExito, setMensajeExito] = useState('');
    const [mostrarVentanaMensaje, setMostrarVentanaMensaje] = useState(false);
    const [hayVentanas, setHayVentanas] = useState(false);

    const handleMostrarVentanaMensaje = () => {
        setMostrarVentanaMensaje(true);
        setHayVentanas(true)
    };
    
    const handleCerrarVentanaMensaje = () => {
        setMostrarVentanaMensaje(false);
        setHayVentanas(false)
    };

    const [nuevoColegial, setNuevoColegial] = useState({
        id_colegial: colegial.id_colegial,
        foto_colegial: colegial.foto_colegial,
        nombre_colegial: colegial.nombre_colegial,
        apellidos_colegial: colegial.apellidos_colegial,
        estudios_colegial: colegial.estudios_colegial,
        pasillo_colegial: colegial.pasillo_colegial,
        promocion_colegial: colegial.promocion_colegial,
        habitacion_colegial: colegial.habitacion_colegial,
        telefono_colegial: colegial.telefono_colegial,
        correo_colegial: colegial.correo_colegial,
        esFavorito_colegial: colegial.esFavorito,
        tieneCargo_colegial: colegial.tieneCargo_colegial
    });

    // Función para manejar cambios en los campos
    const handleChangeCampo = (campo, valor) => {
        setNuevoColegial(prevColegial => ({
            ...prevColegial,
            [campo]: valor
        }));
    };

    const [cambiandoCampos, setCambiandoCampos] = useState(false);
    const handleCambiandoCambios = () => {
        setCambiandoCampos(!cambiandoCampos);
    };
    const [editandoNombre, setEditandoNombre] = useState(false);
    const handleEditandoNombre = (e) => {
        setEditandoNombre(!editandoNombre);
    };
    const [editandoApellidos, setEditandoApellidos] = useState(false);
    const handleEditandoApellidos = (e) => {
        setEditandoApellidos(!editandoApellidos);
    };
    const [editandoEstudios, setEditandoEstudios] = useState(false);
    const handleEditandoEstudios = (e) => {
        setEditandoEstudios(!editandoEstudios);
    };
    const [editandoPasillo, setEditandoPasillo] = useState(false);
    const handleEditandoPasillo = (e) => {
        setEditandoPasillo(!editandoPasillo);
    };
    const [editandoPromocion, setEditandoPromocion] = useState(false);
    const handleEditandoPomocion = (e) => {
        setEditandoPromocion(!editandoPromocion);
    };
    const [editandoTieneCargo, setEditandoTieneCargo] = useState(false);
    const handleEditandoTieneCargo = (e) => {
        setEditandoTieneCargo(!editandoTieneCargo);
    };
    const [editandoHabitacion, setEditandoHabitacion] = useState(false);
    const handleEditandoHabitacion = (e) => {
        setEditandoHabitacion(!editandoHabitacion);
    };
    const [editandoTelefono, setEditandoTelefono] = useState(false);
    const handleEditandoTelefono = (e) => {
        setEditandoTelefono(!editandoTelefono);
    };
    const [editandoCorreo, setEditandoCorreo] = useState(false);
    const handleEditandoCorreo = (e) => {
        setEditandoCorreo(!editandoCorreo);
    };

    //Función para añadir o eliminar un el colegial a favoritos
    const manejarFavoritoLocal = async () => {
        await manejarFavorito(colegial.id_colegial, nuevoColegial.esFavorito_colegial);
        setNuevoColegial(prev => ({ ...prev, esFavorito_colegial: !prev.esFavorito_colegial }));
    };

    const handleFotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                setMensajeError('Solo se permiten archivos JPG y PNG');
                return;
            }
            setMensajeError('');
            const reader = new FileReader();
            reader.onloadend = () => {
                handleChangeCampo('foto_colegial', reader.result);
                handleChangeCampo('foto_nombre', file.name);
            };
            reader.readAsDataURL(file);
        }
    };

    const guardarCambios = async () => {
        const formData = new FormData();
    
        for (const key in nuevoColegial) {
            if (key === 'foto_colegial' && nuevoColegial[key].startsWith('data:')) {
                const response = await fetch(nuevoColegial[key]);
                const blob = await response.blob();
                formData.append('foto_colegial', blob, nuevoColegial.foto_nombre);
            } else {
                formData.append(key, nuevoColegial[key]);
            }
        }
    
        try {
            // console.log("Enviando datos del colegial:", formData);
            // for (let pair of formData.entries()) {
            //     console.log(pair[0] + ': ' + pair[1]);
            // }
            const result = await actualizarColegial(token, formData);
    
            //console.log("Respuesta API: ", result);
    
            if (result.success) {
                setMensajeExito('Datos actualizados con éxito.');
                setCambiandoCampos(false);
                setEditandoNombre(false);
                setEditandoEstudios(false);
                setEditandoPasillo(false);
                setEditandoPromocion(false);
                setEditandoTieneCargo(false);
                setEditandoHabitacion(false);
                setEditandoTelefono(false);
                setEditandoCorreo(false);
                actualizarLista();

            } else {
                setMensajeError(result.message || 'Error al actualizar los datos.');
            }
        } catch (error) {
            setMensajeError('Error al actualizar los datos.');
        }
    };
    

    const eliminarColegialLocal = async () => {
        try {
            const result = await eliminarColegial(nuevoColegial.id_colegial);
            if (result.success) {
              setMensajeExito('Colegial eliminado con éxito.');
              onClose();
            } else {
              setMensajeError(result.message || 'Error al eliminar el colegial.');
            }
        } catch (error) {
            setMensajeError('Error al eliminar el colegial.');
        }
    };

    return (
        <div className="container col-xl-4 col-lg-5 col-md-6 bdr-1 rounded p-0 position-fixed text-center" style={{ fontWeight: '3rem', background: 'black', minHeight:'38rem', top: tienePrivilegios ? '44%' : '46%', left: '50%', transform: 'translate(-50%, -40%)', zIndex:'6', border: '2px solid black' }}>
        {mostrarVentanaMensaje && (
            <VentanaEnviarMensaje
                onClose={handleCerrarVentanaMensaje}
                idColegial={nuevoColegial.id_colegial}
            />
        )}
        <div className='row-fluid' style={{backgroundColor:'white'}}>
            {/*{<img alt="foto_colegial" src={nuevoColegial.foto_colegial.startsWith('data:') ? nuevoColegial.foto_colegial : urlImagenes + nuevoColegial.foto_colegial} style={{ height: '15rem', width: '21rem' }} />*/}
            <img alt="foto_colegial" src={nuevoColegial.foto_colegial.startsWith('data:') ? nuevoColegial.foto_colegial : urlImagenes + nuevoColegial.foto_colegial} style={{ height: '15rem', width: '23rem' }}  />
            {cambiandoCampos && (
                <div className="position-absolute" style={{ top: '12.5rem', right: '1rem', cursor: 'pointer' }}>
                    <input
                        type="file"
                        accept="image/*"
                        className="d-none"
                        id="fotoInput"
                        onChange={handleFotoChange}
                    />
                    <label htmlFor="fotoInput">
                        <img alt="lapiz" src={lapizNegro} style={{ height: '2rem', width: '2rem', cursor: 'pointer' }} />
                    </label>
                </div>
            )}
            <img alt="atras" src={atrasNegro} style={{position: 'absolute', left: 0, height:'3rem', width:'3rem', cursor:'pointer'}} onClick={onClose}/>
            {nuevoColegial.esFavorito_colegial ? 
            ( <><img alt="corazon" src={corazonRojo} style={{position: 'absolute', right: 0,height:'3rem', width:'3rem', cursor:'pointer'}} onClick={manejarFavoritoLocal} /></>  
            ) : ( 
            <><img alt="corazon" src={favoritosNegro} style={{position: 'absolute', right: 0,height:'3rem', width:'3rem', cursor:'pointer'}} onClick={manejarFavoritoLocal} /></> )}
        </div>
        <div className='row-fluid text-start d-flex pb-2 mt-3'>
            <div className='col-12 d-flex'> 
                <span className="customFontSizeTextoMediano ml-3 text-white"> Nombre: </span>
                {editandoNombre ? (                 
                    <form className='ml-3'>
                    <input
                        type="texto"
                        className="form-control text-white bg-transparent"
                        id="nombre_colegial"
                        placeholder={nuevoColegial.nombre_colegial}
                        value={nuevoColegial.nombre_colegial}
                        onChange={(e) => handleChangeCampo("nombre_colegial", e.target.value)}
                        />
                    </form> 
                ) : (
                    <span className=" customFontSizeTextoMediano ml-2" style={{ color: '#808080' }}> {nuevoColegial.nombre_colegial}</span>
                )}
                {cambiandoCampos && <>
                    <div onClick={handleEditandoNombre}>
                        <img alt="corazon" src={lapizBlanco} style={{height:'1.5rem', width:'1.5rem', cursor:'pointer', marginTop:'0.3rem', marginLeft:'0.3rem'}}/>
                    </div>
                </>}
            </div>
        </div>
        <div className='row-fluid text-start d-flex pb-2'>
            <div className='col-12 d-flex'> 
                <span className="customFontSizeTextoMediano ml-3 text-white"> Apellidos: </span>
                {editandoApellidos ? (                 
                    <form className='ml-3'>
                    <input
                        type="texto"
                        className="form-control text-white bg-transparent"
                        id="nombre_colegial"
                        placeholder={nuevoColegial.apellidos_colegial}
                        value={nuevoColegial.apellidos_colegial}
                        onChange={(e) => handleChangeCampo("apellidos_colegial", e.target.value)}
                        />
                    </form> 
                ) : (
                    <span className=" customFontSizeTextoMediano ml-2" style={{ color: '#808080' }}> {nuevoColegial.apellidos_colegial}</span>
                )}
                {cambiandoCampos && <>
                    <div onClick={handleEditandoApellidos}>
                        <img alt="corazon" src={lapizBlanco} style={{height:'1.5rem', width:'1.5rem', cursor:'pointer', marginTop:'0.3rem', marginLeft:'0.3rem'}}/>
                    </div>
                </>}
            </div>
        </div>
        <div className='row-fluid text-start d-flex pb-2'>
            <div className='col-12 d-flex'> 
                <span className="customFontSizeTextoMediano ml-3 text-white"> Estudios: </span>
                {editandoEstudios ? (                 
                    <form className='ml-3'>
                    <input
                        type="texto_colegial"
                        className="form-control text-white bg-transparent"
                        id="estudios"
                        placeholder={nuevoColegial.estudios_colegial}
                        value={nuevoColegial.estudios_colegial}
                        onChange={(e) => handleChangeCampo("estudios_colegial", e.target.value)}
                        />
                    </form> 
                ) : (
                    <span className="text-white customFontSizeTextoMediano ml-2"> {nuevoColegial.estudios_colegial}</span>
                )}
                {cambiandoCampos && <>
                    <div onClick={handleEditandoEstudios}>
                        <img alt="corazon" src={lapizBlanco} style={{height:'1.5rem', width:'1.5rem', cursor:'pointer', marginTop:'0.3rem', marginLeft:'0.3rem'}}/>
                    </div>
                </>}
            </div>
        </div>
        <div className='row-fluid text-start d-flex pb-2'>
            <div className='col-9 d-flex'> 
                <span className="customFontSizeTextoMediano ml-3 text-white"> Pasillo: </span>
                {editandoPasillo ? (                 
                    <form className='ml-3'>
                        <select
                            className="form-control text-white bg-transparent"
                            id="pasillo_colegial"
                            value={nuevoColegial.pasillo_colegial}
                            onChange={(e) => handleChangeCampo("pasillo_colegial", e.target.value)}
                            style={{ backgroundColor: 'black' }}
                        >
                            <option value="" style={{backgroundColor:'black'}}>Seleccionar</option>
                            <option value="100s" style={{backgroundColor:'black'}}>100s</option>
                            <option value="200s" style={{backgroundColor:'black'}}>200s</option>
                            <option value="300s" style={{backgroundColor:'black'}}>300s</option>
                            <option value="400s" style={{backgroundColor:'black'}}>400s</option>
                        </select>
                    </form>
                ) : (
                    <span className="text-white customFontSizeTextoMediano ml-2"> {nuevoColegial.pasillo_colegial}</span>
                )}
                {cambiandoCampos && <>
                    <div onClick={handleEditandoPasillo}>
                        <img alt="corazon" src={lapizBlanco} style={{height:'1.5rem', width:'1.5rem', cursor:'pointer', marginTop:'0.3rem', marginLeft:'0.3rem'}}/>
                    </div>
                </>}
            </div>
        </div>
        <div className='row-fluid text-start d-flex pb-2'>
            <div className='col-8 d-flex'> 
                <span className="customFontSizeTextoMediano ml-3 text-white"> Promocion: </span>
                {editandoPromocion ? (                 
                    <form className='ml-3'>
                        <select
                            className="form-control text-white bg-transparent"
                            id="promocion_colegial"
                            value={nuevoColegial.promocion_colegial}
                            onChange={(e) => handleChangeCampo("promocion_colegial", e.target.value)}
                            style={{ backgroundColor: 'black' }}
                        >
                            <option value="" style={{ backgroundColor: 'black' }}>Seleccionar</option>
                            <option value="Primera" style={{ backgroundColor: 'black' }}>Primera</option>
                            <option value="Segunda" style={{ backgroundColor: 'black' }}>Segunda</option>
                            <option value="Tercera" style={{ backgroundColor: 'black' }}>Tercera</option>
                            <option value="Cuarta" style={{ backgroundColor: 'black' }}>Cuarta</option>
                            <option value="Quinta" style={{ backgroundColor: 'black' }}>Quinta o más</option>
                        </select>
                    </form> 
                ) : (
                    <span className="text-white customFontSizeTextoMediano ml-2"> {nuevoColegial.promocion_colegial}</span>
                )}
                {cambiandoCampos && <>
                    <div onClick={handleEditandoPomocion}>
                        <img alt="corazon" src={lapizBlanco} style={{height:'1.5rem', width:'1.5rem', cursor:'pointer', marginTop:'0.3rem', marginLeft:'0.3rem'}}/>
                    </div>
                </>}
            </div>
        </div>
        {(tienePrivilegios || nuevoColegial.tieneCargo_colegial) && (
        <div className='row-fluid text-start d-flex pb-2'>
            <div className='col-8 d-flex'> 
                <span className="customFontSizeTextoMediano ml-3 text-white"> Habitacion: </span>
                {editandoHabitacion ? (                 
                    <form className='ml-3'>
                    <input
                        type="texto"
                        className="form-control text-white bg-transparent"
                        id="habitacion_colegial"
                        placeholder={nuevoColegial.habitacion_colegial}
                        value={nuevoColegial.habitacion_colegial}
                        onChange={(e) => handleChangeCampo("habitacion_colegial", e.target.value)}
                        />
                    </form> 
                ) : (
                    <span className="text-white customFontSizeTextoMediano ml-2"> {nuevoColegial.habitacion_colegial}</span>
                )}
                {cambiandoCampos && <>
                    <div onClick={handleEditandoHabitacion}>
                        <img alt="corazon" src={lapizBlanco} style={{height:'1.5rem', width:'1.5rem', cursor:'pointer', marginTop:'0.3rem', marginLeft:'0.3rem'}}/>
                    </div>
                </>}
            </div>
        </div>
        )}
        {tienePrivilegios ? (
        <>
            <div className='row-fluid text-start d-flex pb-2'>
                <div className='col-10 d-flex'> 
                    <span className="customFontSizeTextoMediano ml-3 text-white"> Telefono: </span>
                    {editandoTelefono ? (                 
                    <form className='ml-3'>
                    <input
                        type="texto"
                        className="form-control text-white bg-transparent"
                        id="telefono_colegial"
                        placeholder={nuevoColegial.telefono_colegial}
                        value={nuevoColegial.telefono_colegial}
                        onChange={(e) => handleChangeCampo("telefono_colegial", e.target.value)}
                        />
                    </form> 
                ) : (
                    <span className="text-white customFontSizeTextoMediano ml-2"> {nuevoColegial.telefono_colegial}</span>
                )}
                {cambiandoCampos && <>
                    <div onClick={handleEditandoTelefono}>
                        <img alt="corazon" src={lapizBlanco} style={{height:'1.5rem', width:'1.5rem', cursor:'pointer', marginTop:'0.3rem', marginLeft:'0.3rem'}}/>
                    </div>
                </>}
                </div>
            </div>
            <div className='row-fluid text-start d-flex pb-2'>
                <div className='col-12 d-flex'> 
                    <span className="customFontSizeTextoMediano ml-3 text-white"> Correo:</span>
                    {editandoCorreo ? (                 
                    <form className='ml-3'>
                    <input
                        type="texto"
                        className="form-control text-white bg-transparent"
                        id="correo_colegial"
                        placeholder={nuevoColegial.correo_colegial}
                        value={nuevoColegial.correo_colegial}
                        onChange={(e) => handleChangeCampo("correo_colegial", e.target.value)}
                        />
                    </form> 
                ) : (
                    <span className="text-white customFontSizeTextoMediano ml-2"> {nuevoColegial.correo_colegial}</span>
                )}
                {cambiandoCampos && <>
                    <div onClick={handleEditandoCorreo}>
                        <img alt="corazon" src={lapizBlanco} style={{height:'1.5rem', width:'1.5rem', cursor:'pointer', marginTop:'0.3rem', marginLeft:'0.3rem'}}/>
                    </div>
                </>}
                </div>
            </div>
            <div className='row-fluid text-start d-flex pb-2'>
                <div className='col-12 d-flex'>
                    <span className="customFontSizeTextoMediano ml-3 text-white"> Tiene cargo:</span>
                    {editandoTieneCargo ? (
                        <form className='ml-3'>
                            <select
                                className="form-control text-white bg-transparent"
                                id="tieneCargo_colegial"
                                value={nuevoColegial.tieneCargo_colegial}
                                onChange={(e) => handleChangeCampo("tieneCargo_colegial", e.target.value === 'true')}
                                style={{ color: 'black' }}
                            >
                                <option value="false" style={{ color: 'black' }}>No</option>
                                <option value="true" style={{ color: 'black' }}>Sí</option>
                            </select>
                        </form>
                    ) : (
                        <>
                            {nuevoColegial.tieneCargo_colegial ? (
                                <span className="text-white customFontSizeTextoMediano ml-2">Sí</span>
                            ) : (
                                <span className="text-white customFontSizeTextoMediano ml-2">No</span>
                            )}</>
                    )}
                    {cambiandoCampos && <>
                        <div onClick={handleEditandoTieneCargo}>
                            <img alt="corazon" src={lapizBlanco} style={{ height: '1.5rem', width: '1.5rem', cursor: 'pointer', marginTop: '0.3rem', marginLeft: '0.3rem' }} />
                        </div>
                    </>}
                </div>
            </div>
            
            {/**CONSERJERIA */}
            {esConser ? (         
                <div className='row-fluid justify-content-center d-flex pb-2'>
                    <button 
                        type="button" 
                        className="btn bg-white text-dark mt-2 btn-light customFontSizeTextoMediano"
                        onClick={handleMostrarVentanaMensaje}
                    >
                        Enviar mensaje
                    </button>
                </div>
            ) : (    
                <>    
                {/**ADMINISTRACIÓN*/}
                {cambiandoCampos ? (      
                    <div className='row-fluid justify-content-center d-flex pb-2'>          
                        <div>
                            <button type="submit" className="btn bg-white text-dark mt-2 btn-light customFontSizeTextoMediano" onClick={guardarCambios}>Guardar cambios</button>
                        </div> 
                        <div className='ml-3'>
                            <button type="submit" className="btn text-dark mt-2 btn-light customFontSizeTextoMediano" onClick={handleCambiandoCambios}>Cancelar</button>
                        </div> 
                    </div> 
                ) : (   
                    <div>    
                        {(mensajeError || mensajeExito) && (
                        <div className="row-fluid justify-content-center d-flex pb-2">
                            {mensajeError && <div className="alert alert-danger mt-3" role="alert">{mensajeError}</div>}
                            {mensajeExito && <div className="alert alert-success mt-3" role="alert">{mensajeExito}</div>}
                        </div> )}                  
                        <div className='row-fluid d-flex justify-content-center pb-3'>            
                            <div className='mr-4'>
                                <button type="submit" className="btn bg-white text-dark mt-2 btn-light customFontSizeTextoMediano" onClick={handleCambiandoCambios}>Cambiar datos</button>
                            </div> 
                            <div className='ml-1'>
                                <button type="submit" className="btn mt-2 btn-light customFontSizeTextoMediano" style={{backgroundColor:'red', color:'white'}} onClick={eliminarColegialLocal}>Eliminar colegial</button>
                            </div>
                        </div>
                    </div>  )}
                </>
            )}
            {nuevoColegial.tieneCargo_colegial ? (
                    <><img alt="tieneCargo_colegial" src={logoCerbunaBlanco} style={{position: 'absolute', right: '8%', top:'58%', height:'7rem', width:'7rem'}} />
                    </>
                ) : (<></> ) 
            }
        </> 
        ) : (
        <>            
            {nuevoColegial.tieneCargo_colegial ? (
                <><img alt="tieneCargo_colegial" src={logoCerbunaBlanco} style={{position: 'absolute', right: '8%', top:'68%', height:'9rem', width:'9rem'}} />
                {cambiandoCampos && <><img alt="corazon" src={lapizBlanco} style={{height:'1.5rem', width:'1.5rem', cursor:'pointer', marginTop:'0.3rem', marginLeft:'0.3rem'}}/></>}</>
                ) : (<>{cambiandoCampos && <><img alt="corazon" src={lapizBlanco} style={{position: 'absolute', right: '6%', top:'58%', height:'1.5rem', width:'1.5rem', cursor:'pointer'}}/></>}</> ) 
            }
        </> ) 
        }
    {/** Pantalla de overlay para que no se pueda seleccionar ningún objeto o interactuar si hay alguna ventana */}
    {hayVentanas && (
        <div className="overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 2
        }}></div>
    )}
    </div>
    );
};

export default InfoColegial;
